// Rewrite Globo Search & Filter's "Sorry, nothing found for …" popup.
// Runs after Globo renders via the `globoFilterSearchCompleted` window event
// that Globo's CDN bundle dispatches when a search completes.
//
// Bonus: if the term looks like a UPC/GTIN (all digits, 4+ chars), match it
// against the GTIN index and render the results inline inside the search
// drawer so the user can keep typing, editing, or clearing their query without
// the UI blocking them.

(function () {
  'use strict';

  const GTIN_API_BASE = 'https://gtin-apimaxilite.maxilite-lighting.net';
  const GTIN_ALL_ENDPOINT = `${GTIN_API_BASE}/all`;
  const GTIN_QUERY_ENDPOINT = `${GTIN_API_BASE}/gtin`; // legacy per-term fallback
  // 4, not 6: across the live index, 93% of 4-digit numeric queries pin exactly one product and
  // the median result is a single product, so the extra reach costs almost no precision. It buys
  // partial model-number searches like "2505" (27 rows, 1 product). A miss is nearly free — a
  // non-matching term falls through to clearPanel() and renders nothing, and the dataset is
  // already in sessionStorage, so the cost is one filter over ~127 rows and no network at all.
  const GTIN_MIN_DIGITS = 4;
  const INLINE_PANEL_ID = 'mx-gtin-inline-results';
  const PAGE_PANEL_ID = 'mx-gtin-page-results';
  const PANEL_CLASS = 'mx-gtin-panel'; // shared hook: styling + event scoping across both panels
  const GROUP_PREVIEW_ROWS = 3; // rows shown per product before "+N more variants"
  const POPUP_SUPPRESSED_CLASS = 'mx-globo-popup-suppressed';
  const NATIVE_EMPTY_CLASS = 'mx-gtin-native-empty'; // set on .template-search, hides #product-grid
  const PANEL_VIEWPORT_GUTTER = 20;
  const PANEL_MIN_HEIGHT = 180;
  const ENRICH_CONCURRENCY = 4; // parallel /products/*.js fetches during enrichment

  // The whole GTIN index is 127 rows / ~33KB raw (~1.8KB brotli on the wire), so we
  // pull it once per tab-session and filter locally instead of hitting /gtin?q= on
  // every no-result search.
  //
  // sessionStorage (not localStorage) plus a TTL is deliberate: see the note near the
  // bottom of maxilite-product.js about the 30-minute localStorage manifest cache that
  // hid newly published data for half an hour and had to be removed. POST /extract can
  // swap this dataset at any time, so staleness is bounded twice — by the tab's
  // lifetime and by DATASET_TTL_MS. A miss costs ~1.8KB, so the TTL is cheap to lower.
  const DATASET_CACHE_KEY = 'maxilite_gtin_dataset_v1';
  const DATASET_TTL_MS = 30 * 60 * 1000;
  const DATASET_FETCH_TIMEOUT_MS = 4000;
  const DATASET_RETRY_COOLDOWN_MS = 60 * 1000;

  let datasetRows = null;             // in-memory copy, this page view only
  let datasetPromise = null;          // in-flight /all, shared by all concurrent callers
  let datasetFailedAt = 0;            // circuit breaker for a dead /all
  const fallbackPromises = new Map(); // term -> Promise<rows[]|null>, legacy path
  const productCache = new Map();
  let groupSeq = 0; // unique ids for aria-controls; global so the two panels never collide

  // Two panels, two independent lifecycles:
  //
  //  - inline: the typeahead panel, anchored to the search form. Torn down aggressively —
  //    on input, on reset, on drawer close, and whenever Globo reports results.
  //  - page:   the /search results panel, rendered once from the URL term. Nothing the user
  //    does in the search box may remove it, or the results they just navigated to would
  //    vanish the moment they touched the field.
  //
  // `generation` is bumped by every render and every dismissal. Async work (dataset fetch,
  // per-product enrichment) captures it and bails if it changed — the only reliable way to
  // stop an in-flight lookup from repainting a panel the user already dismissed. It lives on
  // the context, not the module, so a typeahead dismissal can't abort the page panel's
  // enrichment pass (and vice versa).
  const INLINE_CTX = { panelId: INLINE_PANEL_ID, mode: 'inline', generation: 0, lastTerm: null };
  const PAGE_CTX = { panelId: PAGE_PANEL_ID, mode: 'page', generation: 0, lastTerm: null };

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isGtinLikeTerm(term) {
    return /^\d+$/.test(term) && term.length >= GTIN_MIN_DIGITS;
  }

  function readDatasetCache() {
    let raw;
    try {
      raw = sessionStorage.getItem(DATASET_CACHE_KEY);
    } catch {
      return null; // private mode / blocked cookies — the access itself throws
    }
    if (!raw) return null;

    let env;
    try {
      env = JSON.parse(raw);
    } catch {
      dropDatasetCache();
      return null;
    }

    // A partial or shape-drifted entry must never be trusted. An empty rows array is
    // never legitimate — the index always has records — so treat it as corruption
    // rather than as "no matches for anything".
    if (
      !env ||
      env.v !== 1 ||
      typeof env.t !== 'number' ||
      !Array.isArray(env.rows) ||
      env.rows.length === 0 ||
      typeof env.rows[0]?.gtin !== 'string'
    ) {
      dropDatasetCache();
      return null;
    }

    if (Date.now() - env.t > DATASET_TTL_MS) {
      dropDatasetCache();
      return null;
    }

    return env.rows;
  }

  function writeDatasetCache(rows) {
    try {
      sessionStorage.setItem(
        DATASET_CACHE_KEY,
        JSON.stringify({ v: 1, t: Date.now(), rows })
      );
    } catch (err) {
      // Quota, private mode, blocked cookies. The in-memory copy still serves this
      // page view; we just refetch on the next navigation. Never fatal.
      console.warn('[maxilite-globo-search-override] dataset cache write skipped:', err);
    }
  }

  function dropDatasetCache() {
    try {
      sessionStorage.removeItem(DATASET_CACHE_KEY);
    } catch {}
  }

  async function fetchDataset() {
    // Without a timeout a cold or wedged container can leave this fetch hanging for the
    // browser default (~90s+), during which EVERY GTIN search blocks behind
    // ensureDataset() — strictly worse than the old per-term lookup. Manual controller
    // rather than AbortSignal.timeout for older-Safari safety.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DATASET_FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(GTIN_ALL_ENDPOINT, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // /all returns a BARE ARRAY. /gtin?q= returns { query, count, results }. Reusing
      // the `data.results` unwrap here would silently yield [] forever. Accept both
      // shapes defensively, but never accept an empty dataset.
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : null;
      if (!rows || rows.length === 0) throw new Error('unexpected /all payload shape');
      return rows;
    } finally {
      clearTimeout(timer);
    }
  }

  // Resolves to the row array, or null if the dataset is unavailable. Never rejects.
  // N concurrent callers share exactly one network request.
  function ensureDataset() {
    if (datasetRows) return Promise.resolve(datasetRows);
    if (datasetPromise) return datasetPromise;

    const cached = readDatasetCache();
    if (cached) {
      datasetRows = cached;
      console.log('[maxilite-globo-search-override] dataset from sessionStorage:', cached.length, 'rows');
      return Promise.resolve(datasetRows);
    }

    // Don't re-attempt a dead /all on every search — that would cost a failed /all AND
    // a /gtin?q= each time. One failure, then straight to fallback for a minute.
    if (datasetFailedAt && Date.now() - datasetFailedAt < DATASET_RETRY_COOLDOWN_MS) {
      return Promise.resolve(null);
    }

    datasetPromise = fetchDataset()
      .then((rows) => {
        datasetRows = rows;
        datasetFailedAt = 0;
        writeDatasetCache(rows);
        console.log('[maxilite-globo-search-override] dataset fetched:', rows.length, 'rows');
        return rows;
      })
      .catch((err) => {
        datasetFailedAt = Date.now();
        console.warn('[maxilite-globo-search-override] /all failed, using /gtin?q= fallback:', err);
        return null; // swallow — ensureDataset never rejects
      })
      .finally(() => {
        // Clear the handle so a post-cooldown retry is possible. Safe: .then above
        // already populated datasetRows before .finally runs.
        datasetPromise = null;
      });

    return datasetPromise;
  }

  // Mirrors /gtin?q= semantics exactly (server.ts):
  //   const needle = q.toLowerCase();
  //   rows.filter(r => r.gtin.toLowerCase().includes(needle)
  //                 || r.sku.toLowerCase().includes(needle))
  // String() coercion is defensive: extract.ts stringifies today, but a numeric gtin
  // from a future sheet change would make .toLowerCase() throw and kill the filter for
  // every row, not just one. Order is preserved so cards render identically to the
  // server response. No trim — the server doesn't trim q either.
  function filterDataset(rows, term) {
    const needle = String(term || '').toLowerCase();
    if (!needle) return []; // an empty needle would includes()-match everything
    return rows.filter((r) => {
      const gtin = String(r?.gtin ?? '').toLowerCase();
      const sku = String(r?.sku ?? '').toLowerCase();
      return gtin.includes(needle) || sku.includes(needle);
    });
  }

  function productUrlFromHandle(handle) {
    const slug = String(handle || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug ? `/products/${slug}` : '#';
  }

  // Find the best container inside the currently-open search drawer.
  // Prefer the <form> that owns the currently-focused / active search input —
  // appending inside the form (but outside Shopify's predictive-search results
  // div, which the <predictive-search> custom element nukes on every render)
  // keeps the panel alive while the user keeps typing.
  // Works for both the header drawer (.search-modal__form) and the /search
  // page's <main-search> form: we just walk up to closest('form') from
  // whatever input Globo decorated.
  // Derived from getActiveSearchInput() so the form we render into is guaranteed to be
  // the same form whose value we validate against. Querying the two independently lets
  // them disagree when more than one search form is on the page.
  function findPanelContainer(ctx) {
    if (ctx.mode === 'page') return findPageContainer();

    const input = getActiveSearchInput();
    const form = input && input.closest('form');
    if (form) return form;

    // Last-resort fallbacks. Note document.body is a landmine: a panel parented there is
    // OUTSIDE <details-modal>, so DetailsModal.onBodyClick sees !this.contains(target)
    // and slams the drawer shut the moment a result is clicked. Unreachable in practice
    // given the fallbacks above, but do not promote it.
    return (
      document.querySelector('.search-modal__content') ||
      document.querySelector('.template-search__search') ||
      document.body
    );
  }

  // /search results page. #ProductGridContainer wraps #product-grid (main-search.liquid) and
  // only exists once search.performed — always true by the time we have a term. The panel is
  // PREPENDED into it so UPC matches sit above whatever Shopify's own search returned,
  // without ever hiding those native hits.
  function findPageContainer() {
    return (
      document.getElementById('ProductGridContainer') ||
      document.querySelector('.template-search__header') ||
      document.querySelector('.template-search')
    );
  }

  // Authoritative search input: the focused one, else the one inside an open drawer,
  // else the /search page's. A page can host more than one search form (header +
  // main-search), so "the first input[type=search]" is not good enough on its own.
  function getActiveSearchInput() {
    return (
      document.querySelector('.gf-autocomplete-input:focus, input[type="search"]:focus') ||
      document.querySelector('details[open] .search-modal__content input[type="search"]') ||
      document.querySelector('.template-search__search input[type="search"]') ||
      document.querySelector('input[type="search"]')
    );
  }

  // Page mode goes to the top of the container, everything else to the bottom.
  function insertPanel(ctx, container, panel) {
    if (ctx.mode === 'page') container.insertBefore(panel, container.firstElementChild);
    else container.appendChild(panel);
  }

  function ensurePanel(ctx, container) {
    let panel = document.getElementById(ctx.panelId);
    if (panel && panel.parentElement !== container && container) {
      // Move to the correct container if the search drawer was re-rendered
      insertPanel(ctx, container, panel);
    }
    if (!panel) {
      panel = document.createElement('div');
      panel.id = ctx.panelId;
      insertPanel(ctx, container || document.body, panel);
    }

    if (ctx.mode === 'page') {
      // #ProductGridContainer carries no gutter of its own — page-width sits on the inner
      // #product-grid — so borrow Dawn's own utility rather than re-deriving the margins.
      panel.className = `${PANEL_CLASS} mx-gtin--page page-width`;
      return panel;
    }

    // The header drawer's .search-modal__content is flex-centered, so an
    // in-flow panel grows the form and shoves the input upward out of view.
    // Detect that context and take the panel out of flow so the input stays
    // put. On /search (main-search), keep it in flow. All the actual styling
    // lives in maxilite-globo-search-override.css.
    const inHeaderModal = !!panel.closest('.search-modal__content');
    if (inHeaderModal && container) {
      // Class, not inline style, so clearPanel can take it back off — otherwise
      // we leave a permanently position:relative form behind.
      container.classList.add('mx-gtin-anchor');
    }
    panel.className = `${PANEL_CLASS} ${inHeaderModal ? 'mx-gtin--overlay' : 'mx-gtin--inline'}`;
    return panel;
  }

  // Dawn computes its predictive-search max-height at runtime the same way
  // (predictive-search.js getResultsMaxHeight). Two deliberate differences:
  //
  //  - We measure the PANEL's own top, not .section-header's bottom. The panel is
  //    absolutely positioned at top:100% of the form, so its top never depends on its
  //    own height, and this stays correct if the header markup changes.
  //  - visualViewport, not innerHeight. This flow always runs with the mobile soft
  //    keyboard up, and on iOS innerHeight does NOT shrink for the keyboard — using it
  //    overstates the space by roughly the keyboard's height and buries the bottom of
  //    the list underneath it.
  //
  // In the in-flow (/search) context there is no such constraint, so we hand height back
  // to the stylesheet rather than pinning a wrong value.
  function applyPanelMaxHeight(panel) {
    if (!panel.classList.contains('mx-gtin--overlay')) {
      panel.style.removeProperty('--mx-gtin-panel-max-height');
      return;
    }
    const vv = window.visualViewport;
    const viewportBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
    const top = panel.getBoundingClientRect().top;
    const avail = Math.round(viewportBottom - top - PANEL_VIEWPORT_GUTTER);
    panel.style.setProperty(
      '--mx-gtin-panel-max-height',
      `${Math.max(PANEL_MIN_HEIGHT, avail)}px`
    );
  }

  // Keep the height honest as the keyboard opens/closes and on rotate. Registered once;
  // a no-op whenever the panel is absent.
  let maxHeightRaf = 0;
  function scheduleMaxHeightUpdate() {
    if (maxHeightRaf) return;
    maxHeightRaf = requestAnimationFrame(() => {
      maxHeightRaf = 0;
      const panel = document.getElementById(INLINE_PANEL_ID);
      if (panel) applyPanelMaxHeight(panel);
    });
  }

  function clearPanel(ctx, reason) {
    ctx.generation += 1; // invalidate every in-flight lookup and enrichment pass
    ctx.lastTerm = null;

    const panel = document.getElementById(ctx.panelId);
    if (panel) {
      const anchor = panel.closest('.mx-gtin-anchor');
      panel.remove();
      if (anchor) anchor.classList.remove('mx-gtin-anchor');
    }
    // Only the typeahead panel ever suppresses Globo's popup, so only it may restore it —
    // the page panel restoring here would un-hide a popup the typeahead is still covering.
    if (ctx.mode === 'inline') restoreGloboPopup();
    // The page panel is gone, so nothing of ours found anything — give the native results
    // block back whatever state it was in.
    if (ctx.mode === 'page') syncNativeResultsVisibility();
    if (reason) console.log(`[maxilite-globo-search-override] ${ctx.mode} panel dismissed:`, reason);
  }

  // Group rows by product handle. A Map preserves insertion order, so the list still
  // mirrors the order the server returned rows in. Broad prefix terms make this matter:
  // "990496" matches all 127 rows but only 15 products, and MXP2505 alone accounts for
  // 27 of them sharing one image and one title.
  function groupByHandle(results) {
    const groups = new Map();
    for (const r of results) {
      const handle = String(r.handle || '').trim();
      // Rows with a blank handle would otherwise all collapse into one bogus group.
      const key = handle || `__nohandle__${String(r.sku || '')}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          handle,
          slug: slugifyHandle(handle),
          title: r.title || handle || '',
          family: r.family || '',
          image: '',
          rows: [],
        });
      }
      const g = groups.get(key);
      if (!g.image) g.image = r.variant_image || r.image || r.image_src || '';
      if (!g.family && r.family) g.family = r.family;
      g.rows.push(r);
    }
    return Array.from(groups.values());
  }

  // Highlight the matched substring so it's obvious WHY a row came back. Each segment is
  // escaped independently and the <mark> is inserted between them, so no user-supplied
  // text ever reaches the DOM unescaped.
  function highlightTerm(value, term) {
    const raw = String(value || '');
    const needle = String(term || '');
    const i = needle ? raw.toLowerCase().indexOf(needle.toLowerCase()) : -1;
    if (i < 0) return escapeHtml(raw);
    return (
      escapeHtml(raw.slice(0, i)) +
      `<mark class="mx-gtin-mark">${escapeHtml(raw.slice(i, i + needle.length))}</mark>` +
      escapeHtml(raw.slice(i + needle.length))
    );
  }

  function buildRowHtml(g, r, index, term) {
    const sku = String(r.sku || '').trim();
    const url = g.slug ? `/products/${g.slug}` : '#';
    const extra = index >= GROUP_PREVIEW_ROWS;

    return (
      `<li class="mx-gtin-row"${extra ? ' data-extra hidden' : ''}>` +
      `<a class="focus-inset" href="${escapeHtml(url)}" data-sku="${escapeHtml(sku)}">` +
      `<span class="mx-gtin-row__sku">${highlightTerm(sku, term)}</span>` +
      `<span class="mx-gtin-row__gtin">${highlightTerm(r.gtin || '', term)}</span>` +
      `</a>` +
      `</li>`
    );
  }

  function buildGroupHtml(g, term, index) {
    const count = g.rows.length;
    const hiddenCount = count - GROUP_PREVIEW_ROWS;
    // groupSeq is global — ids must stay unique across BOTH panels, which can be on the
    // page at the same time on /search.
    const rowsId = `mx-gtin-rows-${++groupSeq}`;
    const moreLabel = `+${hiddenCount} more variant${hiddenCount === 1 ? '' : 's'}`;

    // width/height give the browser an aspect ratio up front so the list doesn't reflow
    // row-by-row as images decode. The first few are eager — they're above the fold in
    // the panel, and lazy costs a visible pop-in exactly where the user is looking.
    // Keyed off the per-render index, not groupSeq: once the page panel has consumed the
    // first N sequence numbers, a groupSeq-based test would lazy-load every image in the
    // typeahead panel that renders afterwards.
    const imgHtml = g.image
      ? `<img class="mx-gtin-group__img" src="${escapeHtml(g.image)}" alt=""` +
        ` width="112" height="112" loading="${index < 3 ? 'eager' : 'lazy'}" decoding="async">`
      : `<span class="mx-gtin-group__img" aria-hidden="true"></span>`;

    return (
      `<li class="mx-gtin-group" data-group="${escapeHtml(g.key)}">` +
      // Deliberately NOT a link. A UPC search is a request for one SPECIFIC variant, so the
      // only links in a group are the variant rows below — enrichment upgrades those to
      // ?variant=<id>. A bare /products/<slug> here would drop the shopper on whichever
      // variant happens to be the default, which is the one thing they didn't search for.
      // No `focus-inset` either: that's Dawn's focus-ring helper for interactive elements.
      `<div class="mx-gtin-group__head">` +
      imgHtml +
      `<span class="mx-gtin-group__text">` +
      `<strong class="mx-gtin-group__title">${escapeHtml(g.title || g.handle)}</strong>` +
      `<span class="mx-gtin-group__family"${g.family ? '' : ' hidden'}>Family: ` +
      `<span class="mx-gtin-group__family-value">${escapeHtml(g.family)}</span></span>` +
      `</span>` +
      `<span class="mx-gtin-group__count">${count}</span>` +
      `</div>` +
      `<ul class="mx-gtin-rows" role="list" id="${rowsId}">` +
      g.rows.map((r, i) => buildRowHtml(g, r, i, term)).join('') +
      `</ul>` +
      (hiddenCount > 0
        ? `<button type="button" class="mx-gtin-more focus-inset" aria-expanded="false"` +
          ` aria-controls="${rowsId}" data-more-label="${escapeHtml(moreLabel)}">${escapeHtml(moreLabel)}</button>`
        : '') +
      `</li>`
    );
  }

  function slugifyHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async function fetchProductJson(slug) {
    if (productCache.has(slug)) return productCache.get(slug);
    console.log('[maxilite-globo-search-override] Shopify product fetch for slug:', slug);
    try {
      const res = await fetch(`/products/${slug}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[maxilite-globo-search-override] Shopify product response for slug:', slug, data);
      productCache.set(slug, data);
      return data;
    } catch (err) {
      console.warn('[maxilite-globo-search-override] /products/*.js fetch failed for', slug, err);
      productCache.set(slug, null);
      return null;
    }
  }

  function familyFromTags(tags) {
    if (!Array.isArray(tags)) return '';
    const tag = tags.find((t) => typeof t === 'string' && t.startsWith('Family_'));
    return tag ? tag.slice('Family_'.length).trim() : '';
  }

  // Takes the grouped structure, so this is one pass per PRODUCT (~15) rather than one
  // per row (127) — same data, an order of magnitude fewer iterations.
  async function enrichCardsFromShopify(ctx, groups, token) {
    let cursor = 0;

    const worker = async () => {
      while (cursor < groups.length) {
        const g = groups[cursor++];
        if (token !== ctx.generation) return; // superseded by a newer render or a dismissal
        if (!g.slug) continue;

        const product = await fetchProductJson(g.slug); // memoized in productCache

        if (token !== ctx.generation) return;
        if (!document.getElementById(ctx.panelId)) return; // panel was removed
        if (!product) continue;

        applyProductToGroup(g, product);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(ENRICH_CONCURRENCY, groups.length) }, worker)
    );
  }

  function applyProductToGroup(g, product) {
    // Node ref captured at render time — no attribute selector, so no escaping concerns
    // for handles like "MX 3392H-T".
    const el = g.el;
    if (!el || !el.isConnected) return;

    // Title from product.title
    if (product.title) {
      const titleEl = el.querySelector('.mx-gtin-group__title');
      if (titleEl) titleEl.textContent = product.title;
    }

    // Family from a "Family_<Name>" tag
    const family = familyFromTags(product.tags);
    const familyEl = el.querySelector('.mx-gtin-group__family');
    const familyValueEl = el.querySelector('.mx-gtin-group__family-value');
    if (familyEl && familyValueEl) {
      if (family) {
        familyValueEl.textContent = family;
        familyEl.hidden = false;
      } else {
        familyEl.hidden = true;
      }
    }

    if (!Array.isArray(product.variants)) return;

    // O(1) per row instead of O(variants) per row: MXP2505 alone has 27 matching rows,
    // and a product can carry hundreds of variants.
    const bySku = new Map();
    for (const v of product.variants) {
      const key = String((v && v.sku) || '').trim().toLowerCase();
      if (key && !bySku.has(key)) bySku.set(key, v);
    }

    // Includes the hidden [data-extra] rows, so expanding later reveals correct hrefs.
    el.querySelectorAll('a[data-sku]').forEach((link) => {
      const variant = bySku.get(String(link.dataset.sku || '').trim().toLowerCase());
      if (!variant || !variant.id) return;
      link.setAttribute('href', `/products/${g.slug}?variant=${variant.id}`);
      link.dataset.variantId = String(variant.id);
    });
  }

  // While our inline panel holds the real answer, suppress Globo's no-result popup so
  // the user isn't looking at two competing messages.
  //
  // Toggled by CLASS, never by inline style. Globo's popup is a jQuery-UI menu that
  // drives its own visibility through inline `display`, so writing to that channel means
  // we can't tell our hiding apart from Globo's — and restoring by clearing the inline
  // style would un-hide a popup Globo itself had hidden, resurrecting "Sorry, nothing
  // found for …" over an empty field. The stylesheet's !important is what lets our class
  // win over Globo's inline display while the panel is up.
  function hideGloboPopup() {
    document.querySelectorAll('.glFilter-search-popup').forEach((el) => {
      el.classList.add(POPUP_SUPPRESSED_CLASS);
    });
  }

  function restoreGloboPopup() {
    document.querySelectorAll(`.${POPUP_SUPPRESSED_CLASS}`).forEach((el) => {
      el.classList.remove(POPUP_SUPPRESSED_CLASS);
    });
  }

  // Globo owns /search at runtime, which the theme source doesn't show: it injects #gf-tree
  // (filter drawer) and #gf-grid > #gf-controls-container (sort dropdown, per-page select,
  // filter trigger, "<b>0</b> Product" summary) into #product-grid, and ADOPTS Dawn's own
  // <ul>, stamping id="gf-products" + data-globo-filter-items on it.
  //
  // Careful with the selector: Globo's sort dropdown is a <ul> of <li role="option">, and its
  // filter tree is full of <li> too. A bare querySelectorAll('li') under #product-grid counts
  // those and concludes there are results. Scope to the product grid itself.
  function nativeResultCount() {
    const grid = document.getElementById('product-grid');
    if (!grid) return 0;
    // All three selectors describe the same node in practice (Dawn's ul, before and after
    // Globo adopts it); querySelectorAll dedupes, so overlap is free insurance.
    return grid.querySelectorAll(
      'ul.product-grid > li, ul[data-globo-filter-items] > li, #gf-products > li'
    ).length;
  }

  // Idempotent, so the one function both hides and reveals. Hides only when OUR panel is the
  // thing that found something and the native search found nothing — never otherwise.
  function syncNativeResultsVisibility() {
    const root = document.querySelector('.template-search');
    // No grid means there is nothing to hide — bail rather than flag the root, which would
    // still collapse .template-search--empty's padding for no reason.
    if (!root || !document.getElementById('product-grid')) return;
    const hide = !!document.getElementById(PAGE_PANEL_ID) && nativeResultCount() === 0;
    root.classList.toggle(NATIVE_EMPTY_CLASS, hide);
  }

  // The count is first read the moment the panel renders, which can be BEFORE Globo has
  // finished mounting. Globo indexes differently from Shopify's native search, so it can turn
  // up products of its own — and hiding those would be wrong. This watcher only ever reveals
  // what a too-early reading hid.
  //
  // rAF-debounced because Globo injects its toolbar node by node. No feedback loop: the
  // observer watches #product-grid while the class goes on .template-search, and attributes
  // aren't observed anyway.
  function watchNativeResults() {
    const grid = document.getElementById('product-grid');
    if (!grid || typeof window.MutationObserver !== 'function') return;

    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        syncNativeResultsVisibility();
      });
    };

    new window.MutationObserver(schedule).observe(grid, { childList: true, subtree: true });
    window.addEventListener('globoFilterSearchCompleted', syncNativeResultsVisibility);
  }

  // Screen readers get nothing from a panel that silently appears. The live region has
  // to live OUTSIDE the panel and persist: a region created in the same tick as its
  // content usually doesn't announce, and rebuilding panel.innerHTML would replace it.
  // Never put aria-live on the panel itself — it would read all 127 rows aloud.
  function announce(message) {
    let status = document.getElementById('mx-gtin-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'mx-gtin-status';
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      document.body.appendChild(status);
    }
    status.textContent = message;
  }

  function renderPanel(ctx, term, results) {
    // A repeat globoFilterSearchCompleted for the same term must not blow away the
    // groups the user has already expanded.
    if (ctx.lastTerm === term && document.getElementById(ctx.panelId)) return;

    const container = findPanelContainer(ctx);
    const panel = ensurePanel(ctx, container);
    const groups = groupByHandle(results);
    const safeTerm = escapeHtml(term);

    panel.innerHTML =
      `<div class="mx-gtin__header">` +
      `Found ${results.length} match${results.length === 1 ? '' : 'es'} by UPC <b>${safeTerm}</b>` +
      (groups.length > 1
        ? `<span class="mx-gtin__subhead">Across ${groups.length} products</span>`
        : '') +
      `</div>` +
      `<ul class="mx-gtin__list" role="list">` +
      groups.map((g, i) => buildGroupHtml(g, term, i)).join('') +
      `</ul>`;

    // Zip DOM nodes onto the group objects in render order, so enrichment never has to
    // build an attribute selector — no CSS escaping, no ambiguity on odd handles.
    const groupEls = panel.querySelectorAll('.mx-gtin-group');
    groups.forEach((g, i) => {
      g.el = groupEls[i] || null;
    });

    applyPanelMaxHeight(panel);
    // Only the typeahead competes with Globo's popup for the same screen space. On the
    // results page what competes is the native/Globo results block below us — hide it if it
    // came back empty, so the shopper isn't reading "0 Product" under a panel full of matches.
    if (ctx.mode === 'inline') hideGloboPopup();
    else syncNativeResultsVisibility();
    ctx.lastTerm = term;
    const token = ++ctx.generation;
    console.log(
      '[maxilite-globo-search-override] Rendered',
      results.length,
      'row(s) in',
      groups.length,
      'group(s) —',
      ctx.mode
    );
    announce(`${results.length} matches across ${groups.length} products for UPC ${term}`);

    // Fire-and-forget: enrich each group with title + family + variant URLs
    // from Shopify's public /products/{handle}.js.
    enrichCardsFromShopify(ctx, groups, token);
  }

  // Degraded path: one request per distinct term, memoized by the promise map so
  // repeated globoFilterSearchCompleted events for the same term collapse into one
  // request. This replaces BOTH the old gtinCache and inFlightTerm — a
  // Map<term, Promise> dedupes and memoizes in a single structure.
  function legacyGtinQuery(term) {
    if (fallbackPromises.has(term)) return fallbackPromises.get(term);

    const p = (async () => {
      const url = `${GTIN_QUERY_ENDPOINT}?q=${encodeURIComponent(term)}`;
      const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data?.results) ? data.results : [];
    })().catch((err) => {
      console.warn('[maxilite-globo-search-override] /gtin?q= fallback failed:', err);
      fallbackPromises.delete(term); // don't memoize a failure — allow a retry
      return null;
    });

    fallbackPromises.set(term, p);
    return p;
  }

  async function tryGtinLookup(ctx, term) {
    // On a cold session ensureDataset() is a real network round trip, so anything can
    // happen while we wait: the user clears the field, closes the drawer, or types a new
    // term. Capture the generation now; every dismissal bumps it, so a stale lookup can
    // tell that its result is no longer wanted.
    const gen = ctx.generation;
    let results;

    const rows = await ensureDataset();
    if (gen !== ctx.generation) return; // dismissed or superseded while we waited

    if (rows) {
      results = filterDataset(rows, term);
      console.log('[maxilite-globo-search-override] local match for', term, '->', results.length, 'result(s)');
    } else {
      results = await legacyGtinQuery(term);
      if (results === null) return; // both paths down — leave the UI untouched
      if (gen !== ctx.generation) return;
    }

    // Validate against the live input, never against Globo's popup DOM. The old guard
    // compared getCurrentPopupTerm() to `term` — but both come from the same Globo
    // markup, so it was comparing Globo against itself and could not detect the one
    // case that matters: the user cleared the field mid-flight.
    //
    // Page mode is exempt: its term comes from the URL, which is authoritative and cannot
    // change without a navigation. A shopper who starts typing a new query while the page
    // lookup is still in flight must not cancel the results they navigated here for.
    if (ctx.mode === 'inline') {
      const input = getActiveSearchInput();
      if (input && (input.value || '').trim() !== term) {
        console.log('[maxilite-globo-search-override] Term changed mid-lookup, skipping render');
        return;
      }
    }

    if (results.length) renderPanel(ctx, term, results);
    else clearPanel(ctx, 'no gtin matches');
  }

  function customizeNoResult() {
    const wrapper = document.querySelector(
      '.glFilter-search-popup .gf-search-no-result .ui-menu-item-wrapper'
    );

    // No no-result node means Globo FOUND something this time, so our typeahead panel is
    // stale by definition. An unconditional `return` here would leave it up.
    //
    // Scoped to INLINE_PANEL_ID on purpose: this function also runs once on `load`, which
    // on /search happens right as the page panel is going up. Checking for either panel
    // would tear the page results down every time Globo completes a search.
    if (!wrapper) {
      if (document.getElementById(INLINE_PANEL_ID)) clearPanel(INLINE_CTX, 'globo returned results');
      return;
    }

    const term = (wrapper.querySelector('b')?.textContent || '').trim();
    const safeTerm = escapeHtml(term);

    console.log('[maxilite-globo-search-override] Rewriting no-result popup for term:', term);

    // --- Edit the copy below freely. Use `safeTerm` to include the search word. ---
    wrapper.innerHTML =
      `We couldn't find anything for <b>${safeTerm}</b>`;
    // ---------------------------------------------------------------------------

    const host = wrapper.closest('.gf-search-no-result');
    if (host) {
      host.setAttribute('aria-label', `We couldn't find anything for ${term}`);
    }

    document
      .querySelectorAll('.ui-helper-hidden-accessible > div')
      .forEach((el) => {
        if (el.textContent.trim().startsWith('Sorry, nothing found for')) {
          el.textContent = `We couldn't find anything for ${term}`;
        }
      });

    // Everything above rewrites Globo's own copy and must run unconditionally.
    // Everything below decides whether OUR panel should exist.
    //
    // `term` comes from Globo's popup markup, which lags the real input — most
    // visibly after the reset (X) button clears the field programmatically, leaving
    // the old term sitting in Globo's DOM. Trusting it alone means a later
    // globoFilterSearchCompleted re-renders the panel moments after the user cleared
    // the search. Trust the input; treat the popup as a hint.
    const input = getActiveSearchInput();
    const liveTerm = input ? (input.value || '').trim() : '';

    if (!liveTerm || liveTerm !== term) {
      clearPanel(INLINE_CTX);
      return;
    }

    // Clear any stale inline panel whenever the term changes
    if (INLINE_CTX.lastTerm && INLINE_CTX.lastTerm !== term) clearPanel(INLINE_CTX);

    if (isGtinLikeTerm(term)) {
      tryGtinLookup(INLINE_CTX, term);
    } else {
      // Non-GTIN term — remove any lingering panel from a previous GTIN search
      clearPanel(INLINE_CTX);
    }
  }

  // Every dismissal hook below targets INLINE_CTX only. That is the whole reason the page
  // panel is a separate instance: on /search the shopper's own search box sits directly
  // above the results, and typing in it must not delete the results they navigated to.

  // Clear the inline panel when the user edits the search input, so stale
  // results don't linger mid-typing. Covers manual backspacing only — see the
  // `reset` listener below for the clear (X) button.
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!t || !t.matches || !t.matches('input[type="search"], .gf-autocomplete-input')) return;
    const val = (t.value || '').trim();
    if (val !== INLINE_CTX.lastTerm) clearPanel(INLINE_CTX);
  });

  // The clear (X) button. Dawn's search-form.js onFormReset() calls preventDefault()
  // and then assigns `this.input.value = ''` programmatically — a programmatic value
  // assignment fires NO `input` event, and preventDefault kills the native reset too.
  // The bubbling `reset` event is the only signal we get.
  document.addEventListener('reset', (e) => {
    const t = e.target;
    if (t && t.matches && t.matches('form')) clearPanel(INLINE_CTX);
  });

  // Drawer close. details-modal.js close() only does removeAttribute('open') and
  // dispatches nothing custom, but the UA still fires a native `toggle` on the
  // <details>. `toggle` does not bubble — however, non-bubbling events still traverse
  // the CAPTURE phase from the root down to the target, which is the same reason
  // addEventListener('focus', fn, true) works for delegation. Hence the `true`.
  // This single hook covers every close path: Escape, the close button, an outside /
  // .modal-overlay click, and the sticky header's scroll-down closeSearchModal().
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!d || d.tagName !== 'DETAILS') return;
      if (d.hasAttribute('open')) return; // opening, not closing
      const panel = document.getElementById(INLINE_PANEL_ID);
      if (!panel) return;
      if (d.closest(`#${INLINE_PANEL_ID}`)) return; // never react to our own markup
      if (!d.contains(panel)) return; // some other <details> on the page
      clearPanel(INLINE_CTX);
    },
    true
  );

  // "+N more variants". Delegated because the panel's innerHTML is rebuilt on every
  // render, so handlers bound to individual buttons would not survive. preventDefault is
  // belt-and-braces on top of type="button" — the typeahead panel lives inside the search
  // <form>, and a submitting button there would navigate to /search?q=…
  // Scoped to .mx-gtin-panel rather than one id, so it drives both panels.
  document.addEventListener('click', (e) => {
    const t = e.target;
    const btn = t && t.closest && t.closest('.mx-gtin-more');
    if (!btn || !btn.closest(`.${PANEL_CLASS}`)) return;
    e.preventDefault();

    const group = btn.closest('.mx-gtin-group');
    if (!group) return;

    // `row.hidden` (the property) rather than a class: it removes the rows from the
    // accessibility tree and the tab order too, not just from view.
    const wasExpanded = btn.getAttribute('aria-expanded') === 'true';
    group.querySelectorAll('.mx-gtin-row[data-extra]').forEach((row) => {
      row.hidden = wasExpanded;
    });
    btn.setAttribute('aria-expanded', String(!wasExpanded));
    btn.textContent = wasExpanded ? btn.dataset.moreLabel : 'Show fewer';
    // Collapsing can strand the button off-screen. Guarded: scrollIntoView is absent in
    // some non-browser DOMs, and this must never break the toggle itself.
    if (wasExpanded && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ block: 'nearest' });
    }
  });

  // Keep the panel height honest as the mobile keyboard opens/closes and on rotate.
  window.addEventListener('resize', scheduleMaxHeightUpdate, { passive: true });
  window.addEventListener('orientationchange', scheduleMaxHeightUpdate, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleMaxHeightUpdate, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleMaxHeightUpdate, { passive: true });
  }

  // Warm the dataset on the first focus of any search input. Focus is the earliest
  // reliable intent signal and lands seconds before Globo finishes a search, so the rows
  // are resident by the time we need them. `focusin`, not `focus`, because focus does not
  // bubble. Idempotent — ensureDataset() is the dedupe, so a missed listener is harmless;
  // tryGtinLookup awaits it too, which covers deep links that never fire a focus event.
  function onSearchFocus(e) {
    const t = e.target;
    if (!t || !t.matches || !t.matches('input[type="search"], .gf-autocomplete-input')) return;
    document.removeEventListener('focusin', onSearchFocus);
    ensureDataset();
  }
  document.addEventListener('focusin', onSearchFocus);

  // Pressing Enter submits the search form, which is a plain GET to /search?q=… — Globo's
  // typeahead popup never exists there, so customizeNoResult() has nothing to hook and the
  // UPC results the user just saw disappear. Shopify's own search doesn't index these
  // GTINs, so the page renders empty. Seed the lookup from the URL instead and put the
  // same panel above the native grid.
  function initSearchPageResults() {
    const root = document.querySelector('.template-search');
    if (!root) return; // not the search template

    // Prefer the Liquid-rendered input (value="{{ search.terms | escape }}") — it is the
    // term Shopify actually searched for, already decoded. URLSearchParams is the fallback
    // for markup changes.
    const input = root.querySelector('input[name="q"]');
    const term = (
      (input && input.value) ||
      new URLSearchParams(window.location.search).get('q') ||
      ''
    ).trim();

    if (!isGtinLikeTerm(term)) return;

    // Gated behind isGtinLikeTerm on purpose: an ordinary search never installs the observer.
    watchNativeResults();

    // No match leaves the page exactly as Shopify rendered it: tryGtinLookup falls through
    // to clearPanel(), which removes a panel that was never created.
    tryGtinLookup(PAGE_CTX, term);
  }

  window.addEventListener('globoFilterSearchCompleted', customizeNoResult);

  if (document.readyState === 'complete') {
    customizeNoResult();
  } else {
    window.addEventListener('load', customizeNoResult);
  }

  // The script is deferred, so the document is parsed by the time this runs — no need to
  // wait for `load` (and its image/font traffic) before starting the lookup.
  initSearchPageResults();
})();
